import { ArrayKeyValueObject } from "@/UI/Interfaces/IBaseUI";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import produce from "immer";

export interface ILayout {
  layout: string,
  insightarea: string,
  widgetgroupno: string,
}

interface IGoal {
  goalno: string,
  goalstxt: string,
  details: string,
  goallevel: string,
  status: string
}

export interface LeaveRequest {
  prid: number,
  employeeno: string,
  employeestxt: string,
  isreferred: boolean,
  leavetypeno: string,
  leaveduration: string,
  datefrom: Date,
  dateto: Date,
  notes: string
}

export interface AttendanceRequest {
  prid: number,
  employeeno: string,
  employeestxt: string,
  isreferred: boolean,
  headertext: string,
  attendancerequestdate: Date,
  attendancetimein: Date,
  attendancetimeout: Date,
}

interface IHome {
  dataLoaded: boolean,
  userObjects: Array<IUserMenu>,
  userObjectsLoading: boolean,
  entryFormObjects: Array<IEntryFormMenu>,
  entryFormObjectsLoading: boolean,
  dataAnalyticsObjects: Array<IDataAnalyticsMenu>,
  dataAnalyticsObjectsLoading: boolean,
  reportObjects: Array<IReportsMenu>,
  reportObjectsLoading: boolean,
  sideBarState: "pinned" | "unpinned",
  announcements: Array<{
    announcementstxt: string,
    announcementltxt: string,
    announcementimage: string,
  }>,
  userTodayActivity: Array<IUserTodayActivity>,
  frequentlyUsed: Array<{
    objectcontrollerno: string,
    objectcontrollerstxt: string,
    moduleno: string,
    modulestxt: string
  }>,
  goals: Array<IGoal>,
  SelfService: {
    employeeData: {
      employeeno: string,
      employeestxt: string,
      employeeimage: string,
      email: string,
      positionstxt: string,
      departmentstxt: string,
      functionalareastxt: string,
      joiningdate: string;
    },
    attendanceData: {
      date: string,
      timein: string,
      timeout: string
    }[],
    portalAttendanceLog: {
      attendanceactivityno: number,
      attendanceactivitydatetime: string,
    }[],
    leaveData: {
      leavedate: string,
      notes: string,
      leavetypeno: string,
      leavetypestxt: string
    }[],
    leaveRequests: LeaveRequest[],
    attendanceRequests: AttendanceRequest[],
    weekHolidays: number[],
    holidayData: {
      date: string,
      heading: string,
      body: string
    }[],
    myTeam: {
      fullname: string,
      employeeimage: string,
      positionstxt: string,
      employeeno: string,
      reportingto: string
    }[],
    geoLocationData: {
      locationidno: string,
      locationstxt: string,
      address: string,
      geofencinglatitude: number,
      geofencinglongitude: number,
      radiusmeters: number
    }[],
    leaveBalance: {
      leavetypestxt: string;
      leavetypeno: string;
      balance: number;
    }[],
    myTask: {
      taskno: string;
      taskstxt: string;
      tasksubject: string;
      duedate: Date;
      istaskcompleted: boolean;
      taskstatushdr: string;
      prioritystxt: string;
    }[],
    myTeamAtt: {
      employeeno: string;
      employeestxt: string;
      employeeimage: string;
      positionstxt: string;
      attendancestatus: string;
    }[],
  },
  designMode: boolean,
  userWidgets: Array<string>,
  userLayout: ArrayKeyValueObject,
  teamCount: number,
  selectedEmployeeNo: string,
  employees: Array<{
    employeeno: string,
    employeestxt: string,
    employeeimage: string,
    positionstxt: string
  }>,
  orgData: {
    fullname: string,
    userphoto: string,
    positionstxt: string,
    employeeno: string,
    reportingto: string
  }[]
}
interface IUserTodayActivity {
  objectcontrollerstxt: string,
  objectcontrollerno: string,
  applicationobjecttype: string,
  objectactivityno: string,
  activitydatetime: string,
  parameterdocvalue: string,
  parameterprid: number,
}

interface IUserMenu {
  moduleno: string,
  modulestxt: string,
  groupindex: number,
  groupstxt: string,
  objectcontrollerno: string,
  objectcontrollerstxt: string,
  processcodeindex: number,
  objectcategorystxt: string,
  applicationobjecttype: string,
  isfavorite: boolean,
  issetting: boolean,
}

interface IEntryFormMenu {
  moduleno: string,
  modulestxt: string,
  groupindex: number,
  groupstxt: string,
  objectcontrollerno: string,
  objectcontrollerstxt: string,
  processcodeindex: number,
  objectcategorystxt: string,
}
interface IDataAnalyticsMenu {
  moduleno: string,
  modulestxt: string,
  objectcontrollerno: string,
  objectcontrollerstxt: string,
  objectcategorystxt: string,
}
interface IReportsMenu {
  moduleno: string,
  modulestxt: string,
  objectcontrollerno: string,
  objectcontrollerstxt: string,
  objectcategorystxt: string,
}

const InitialState: IHome = {
  userObjects: [],
  userObjectsLoading: false,
  entryFormObjects: [],
  entryFormObjectsLoading: false,
  dataAnalyticsObjects: [],
  dataAnalyticsObjectsLoading: false,
  reportObjects: [],
  reportObjectsLoading: false,
  sideBarState: "pinned",
  announcements: [],
  userTodayActivity: [],
  frequentlyUsed: [],
  goals: [],
  SelfService: {
    employeeData: {
      employeeno: "",
      employeestxt: "",
      employeeimage: "",
      email: "",
      positionstxt: "",
      departmentstxt: "",
      joiningdate: "1900-01-01T00:00:00",
      functionalareastxt: ""
    },
    attendanceData: [],
    portalAttendanceLog: [],
    leaveData: [],
    leaveRequests: [],
    attendanceRequests: [],
    weekHolidays: [],
    holidayData: [],
    myTeam: [],
    geoLocationData: [],
    leaveBalance: [],
    myTask: [],
    myTeamAtt: []
  },
  dataLoaded: false,
  designMode: false,
  userWidgets: ["selfservice", "activeapproval", "favorites", "frequentlyused", "mytasks", "mygoals", "events", "announcements"],
  userLayout: [
    {
      w: 12,
      h: 25,
      x: 0,
      y: 0,
      i: 'selfservice',
      moved: false,
      'static': true
    },
    {
      w: 12,
      h: 8,
      x: 0,
      y: 19,
      i: "activeapproval",
      moved: false,
      'static': false
    },
    {
      w: 6,
      h: 8,
      x: 0,
      y: 37,
      i: "favorites",
      moved: false,
      'static': false
    },
    {
      w: 6,
      h: 8,
      x: 7,
      y: 37,
      i: "frequentlyused",
      moved: false,
      'static': false
    },
    {
      w: 6,
      h: 8,
      x: 0,
      y: 28,
      i: "mytasks",
      moved: false,
      'static': false
    },
    {
      w: 6,
      h: 8,
      x: 7,
      y: 28,
      i: "mygoals",
      moved: false,
      'static': false
    },
    {
      w: 6,
      h: 8,
      x: 0,
      y: 44,
      i: "events",
      moved: false,
      'static': false
    },
    {
      w: 6,
      h: 8,
      x: 7,
      y: 44,
      i: "announcements",
      moved: false,
      'static': false
    }
  ],
  teamCount: 0,
  selectedEmployeeNo: "",
  employees: [],
  orgData: []
};

const HomeSlice = createSlice({
  name: "home",
  initialState: InitialState,
  reducers: {
    userAttendanceMark(state, action: PayloadAction<{
      type: "timein" | "timeout",
      latitude: number,
      longitude: number,
      address: string
    }>) { },
    getHomeUserSpecificData() { },
    setHomeUserSpecificData(state, action: PayloadAction<{ userTodayActivity: Array<IUserTodayActivity>, announcements: Array<any>, frequentlyUsed: Array<any>, employees: Array<any>, teamCount: number, orgData: Array<any>, goals: Array<IGoal> }>) {
      const payload = action.payload;
      return produce(state, (draftState) => {
        draftState.userTodayActivity = payload.userTodayActivity;
        draftState.announcements = payload.announcements;
        draftState.frequentlyUsed = payload.frequentlyUsed;
        draftState.employees = payload.employees;
        draftState.teamCount = payload.teamCount;
        draftState.orgData = payload.orgData;
        draftState.goals = payload.goals;
        draftState.dataLoaded = true;
      });
    },

    getUserObjects(state) {
      return produce(state, (draftState) => {
        draftState.entryFormObjectsLoading = true;
      });
    },
    setUserObjects(state, action: PayloadAction<{ userObjects: Array<IUserMenu> }>) {
      const payload = action.payload;
      return produce(state, (draftState) => {
        draftState.userObjectsLoading = false;
        draftState.userObjects = payload.userObjects;
      });
    },

    getUserEntryForms(state) {
      return produce(state, (draftState) => {
        draftState.entryFormObjectsLoading = true;
      });
    },
    setUserEntryForms(state, action: PayloadAction<{ userEntryForms: Array<IEntryFormMenu> }>) {
      const payload = action.payload;
      return produce(state, (draftState) => {
        draftState.entryFormObjectsLoading = false;
        draftState.entryFormObjects = payload.userEntryForms;
      });
    },

    getUserDataAnalytics(state) {
      return produce(state, (draftState) => {
        draftState.dataAnalyticsObjectsLoading = true;
      });
    },
    setUserDataAnalytics(state, action: PayloadAction<{ userDataAnalytics: Array<IDataAnalyticsMenu> }>) {
      const payload = action.payload;
      return produce(state, (draftState) => {
        draftState.dataAnalyticsObjectsLoading = false;
        draftState.dataAnalyticsObjects = payload.userDataAnalytics;
      });
    },

    getUserReport(state) {
      return produce(state, (draftState) => {
        draftState.reportObjectsLoading = true;
      });
    },
    setUserReport(state, action: PayloadAction<{ userReport: Array<IReportsMenu> }>) {
      const payload = action.payload;
      return produce(state, (draftState) => {
        draftState.reportObjectsLoading = false;
        draftState.reportObjects = payload.userReport;
      });
    },

    refreshHomeData(state) {
      return produce(state, (draftState) => {
        draftState.dataLoaded = false;
      })
    },

    setUserFavorite(state, action: PayloadAction<{ objectcontrollerno: string, objectcontrollerstxt: string, applicationobjecttype: string }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.userObjects = draftState.userObjects.map(x => {
          if (x.objectcontrollerno == payload.objectcontrollerno) {
            x.isfavorite = !x.isfavorite
          }

          return x;
        });
      })
    },

    setSideBarState(state, action: PayloadAction<"pinned" | "unpinned">) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.sideBarState = payload;
      })
    },

    toggleDesignMode(state, action: PayloadAction<boolean>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.designMode = payload;
      })
    },

    storeHomeLayout(state, action: PayloadAction<{ widgets: Array<string>, layout: ArrayKeyValueObject }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.userWidgets = payload.widgets;
        draftState.userLayout = payload.layout;
      })
    },

    getSelfServiceData() { },
    setAllSelfServiceData(state, action) {
      const payload = action.payload;
      return produce(state, (draftState) => {
        draftState.SelfService = payload;
      });
    },
    updateAttendanceData(state, action: PayloadAction<{
      date: Date,
      time: Date,
      type: "timein" | "timeout",
      latitude: number,
      longitude: number,
      address: string
    }>) {
      const payload = action.payload;
      return produce(state, (draftState) => {

        const date = new Date(payload.date.getFullYear(), payload.date.getMonth(), payload.date.getDate());
        let found = false;
        draftState.SelfService.attendanceData.map(x => {
          let attendanceDataTime = new Date(x.date);
          let attendanceData = new Date(attendanceDataTime.getFullYear(), attendanceDataTime.getMonth(), attendanceDataTime.getDate());

          if (found == false && attendanceData.getTime() == date.getTime()) {
            found = true;
            if (payload.type == "timein") {
              x.timein = payload.time.toISOString();
            }
            else {
              x.timeout = payload.time.toISOString();
            }
          }
        });

        if (!found) {
          draftState.SelfService.attendanceData.push({
            date: date.toISOString(),
            timein: payload.type == "timein" ? payload.time.toISOString() : "",
            timeout: payload.type == "timeout" ? payload.time.toISOString() : ""
          });
        }

        draftState.SelfService.portalAttendanceLog.push({
          attendanceactivityno: payload.type == "timein" ? 0 : 1,
          attendanceactivitydatetime: payload.time.toISOString()
        })

      });
    },
    insertNewAttendanceData(state, action: PayloadAction<IHome["SelfService"]["attendanceData"]>) {
      const payload = action.payload;
      return produce(state, (draftState) => {

        draftState.SelfService.attendanceData.push(...payload);

      });
    },

    selectEmployeeNo(state, action: PayloadAction<string>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.selectedEmployeeNo = payload;
      })
    },

    saveLeaveRequest(state, action: PayloadAction<LeaveRequest>) {
    },
    addLeaveRequest(state, action: PayloadAction<LeaveRequest>) {
      return produce(state, (draftState) => {
        const findIdx = draftState.SelfService.leaveRequests.findIndex(x => x.prid == action.payload.prid);

        if (findIdx != -1) {
          draftState.SelfService.leaveRequests[findIdx] = action.payload;
        }
        else {
          draftState.SelfService.leaveRequests.push(action.payload);
        }
      })
    },

    saveAttendanceRequest(state, action: PayloadAction<AttendanceRequest>) {
    },
    addAttendanceRequest(state, action: PayloadAction<AttendanceRequest>) {
      return produce(state, (draftState) => {
        const findIdx = draftState.SelfService.attendanceRequests.findIndex(x => x.prid == action.payload.prid);

        if (findIdx != -1) {
          draftState.SelfService.attendanceRequests[findIdx] = action.payload;
        }
        else {
          draftState.SelfService.attendanceRequests.push(action.payload);
        }
      })
    }
  },
});

export const { userAttendanceMark, getUserObjects, setUserObjects, getUserEntryForms, setUserEntryForms, getUserDataAnalytics, setUserDataAnalytics, getUserReport, setUserReport, getHomeUserSpecificData, setHomeUserSpecificData, refreshHomeData, setUserFavorite, setSideBarState, toggleDesignMode, storeHomeLayout, getSelfServiceData, setAllSelfServiceData, updateAttendanceData, insertNewAttendanceData, selectEmployeeNo, saveLeaveRequest, addLeaveRequest, saveAttendanceRequest, addAttendanceRequest } = HomeSlice.actions;

export default HomeSlice.reducer;
